import { gsap } from "gsap";

// ── Proof section: counter animation & flag reveal ─────────────────────────
(function () {
    function easeOutQuart(t: number): number {
        return 1 - Math.pow(1 - t, 4);
    }

    function formatNumber(value: number): string {
        return value.toLocaleString("en-US");
    }

    function animateCounter(el: HTMLElement): void {
        const target = parseInt(el.dataset.counter!, 10);
        const suffix = el.dataset.suffix ?? "";
        const duration = Math.min(900 + Math.sqrt(target) * 4, 2200);
        let startTime: number | null = null;

        function step(timestamp: number): void {
            if (!startTime) {
                startTime = timestamp;
            }
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const current = Math.round(easeOutQuart(progress) * target);
            el.textContent = formatNumber(current) + suffix;
            if (progress < 1) {
                requestAnimationFrame(step);
            }
        }

        el.textContent = "0" + suffix;
        requestAnimationFrame(step);
    }

    const section = document.querySelector<HTMLElement>(".welcome-page.proof-section");
    if (!section || !("IntersectionObserver" in window)) {
        return;
    }

    const flags = section.querySelectorAll<HTMLElement>(".flag-strip .flag-icon");
    flags.forEach(flag => {
        flag.style.opacity = "0";
        flag.style.transform = "translateY(8px)";
    });

    const observer = new IntersectionObserver(
        entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) {
                    return;
                }
                section.querySelectorAll<HTMLElement>("[data-counter]").forEach(animateCounter);
                flags.forEach((flag, i) => {
                    flag.style.animationDelay = i * 25 + "ms";
                    flag.classList.add("is-revealed");
                });
                observer.unobserve(section);
            });
        },
        { threshold: 0.3 },
    );

    observer.observe(section);
})();

// ── Constants ──────────────────────────────────────────────────────────────
const URLS = [
    "martialmatch.com/en/manage-events",
    "martialmatch.com/en/pages/admin/federations/12/create-event-wizard",
    "martialmatch.com/en/events/2847-european-open-2025",
    "martialmatch.com/en/pages/admin/events/2847/competitors",
    "martialmatch.com/en/pages/admin/events/2847/brackets-listing",
    "martialmatch.com/en/pages/admin/events/2847/live-scoreboards/3",
    "martialmatch.com/en/events/2847-european-open-2025/results",
];

const STEPS: [number, number][] = [
    // [index, dwell duration in seconds]
    [0, 2.5], // cursor click animation
    [1, 1.2],
    [2, 0.9],
    [3, 1.1],
    [4, 0.8],
    [5, 3.0], // scoreboard animates during dwell
    [6, 2.5], // results stagger during dwell
];

// ── DOM references ─────────────────────────────────────────────────────────
const clSteps = [0, 1, 2, 3, 4, 5, 6].map(i => document.getElementById("clStep" + i));
const windows = [0, 1, 2, 3, 4, 5, 6].map(i => document.getElementById("win" + i));
const urlEl = document.getElementById("workflowUrl");
const scoreRedEl = document.getElementById("scoreRed");
const scoreBlueEl = document.getElementById("scoreBlue");
const timerEl = document.getElementById("matchTimer");
const resultRows = [1, 2, 3].map(i => document.getElementById("resultRow" + i));
const resultsPublishedEl = document.getElementById("resultsPublished");
const organizeBtnEl = document.getElementById("organizeBtn");
const winCursorEl = document.getElementById("winCursor");

// ── Helper functions ───────────────────────────────────────────────────────
function formatTimer(totalSeconds: number): string {
    const s = Math.round(totalSeconds);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m + ":" + (sec < 10 ? "0" : "") + sec;
}

function activate(i: number): void {
    clSteps[i]!.classList.add("is-active");
}

function complete(i: number): void {
    clSteps[i]!.classList.remove("is-active");
    clSteps[i]!.classList.add("is-complete");
}

function resetAll(): void {
    clSteps.forEach(s => s!.classList.remove("is-active", "is-complete"));

    gsap.set(windows[0], { xPercent: 0, opacity: 1 });
    for (let i = 1; i < windows.length; i++) {
        gsap.set(windows[i], { xPercent: 100, opacity: 0 });
    }

    urlEl!.textContent = URLS[0];

    gsap.set(winCursorEl!, { opacity: 0, scale: 1, x: 0, y: 0 });
    gsap.set(organizeBtnEl!, { scale: 1 });

    scoreRedEl!.textContent = "0";
    scoreBlueEl!.textContent = "0";
    timerEl!.textContent = "3:00";
    scoreObj.red = 0;
    scoreObj.blue = 0;
    timerObj.val = 180;

    resultRows.forEach(r => gsap.set(r!, { opacity: 0, y: 8 }));
    resultsPublishedEl!.classList.remove("is-visible");
    gsap.set(resultsPublishedEl!, { opacity: 0 });
}

// ── State objects for numeric tweening ────────────────────────────────────
const scoreObj = { red: 0, blue: 0 };
const timerObj = { val: 180 };

function hasRequiredElements(): boolean {
    return (
        clSteps.every(stepEl => stepEl !== null) &&
        windows.every(windowEl => windowEl !== null) &&
        resultRows.every(rowEl => rowEl !== null) &&
        !!urlEl &&
        !!scoreRedEl &&
        !!scoreBlueEl &&
        !!timerEl &&
        !!resultsPublishedEl &&
        !!organizeBtnEl &&
        !!winCursorEl
    );
}

// Guard: bail silently if the widget is not on this page
if (!hasRequiredElements() || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    // widget not present — nothing to animate
} else {
    document.getElementById("workflowWidget")?.classList.add("is-animated");
    const cursorTarget = { x: 0, y: 0 };

    // ── Initial state ────────────────────────────────────────────────────
    // All steps start with is-complete in HTML (JS-disabled fallback).
    // Strip that here so the animation starts from pending state.
    clSteps.forEach(s => s!.classList.remove("is-complete"));

    // Window 0 visible, rest off-screen to the right
    gsap.set(windows[0], { xPercent: 0, opacity: 1 });
    for (let i = 1; i < windows.length; i++) {
        gsap.set(windows[i], { xPercent: 100, opacity: 0 });
    }

    // Cursor starts hidden
    gsap.set(winCursorEl, { opacity: 0 });

    // Pre-hide result rows and published badge
    resultRows.forEach(r => gsap.set(r!, { opacity: 0, y: 8 }));
    gsap.set(resultsPublishedEl, { opacity: 0 });

    // ── Build master timeline ────────────────────────────────────────────
    const tl = gsap.timeline({ repeat: -1, repeatDelay: 0, onRepeat: resetAll });

    STEPS.forEach(([i, dwell]) => {
        // 1. Activate checklist item
        tl.call(activate, [i]);

        // 2. Slide window transition (skip for first step — already visible)
        if (i > 0) {
            tl.to(
                windows[i - 1],
                {
                    xPercent: -100,
                    opacity: 0,
                    duration: 0.7,
                    ease: "power2.inOut",
                },
                "<",
            );
            tl.fromTo(windows[i], { xPercent: 100, opacity: 0 }, { xPercent: 0, opacity: 1, duration: 0.7, ease: "power2.inOut" }, "<");
        }

        // 3. Fade URL to new value
        tl.to(urlEl!, { opacity: 0, duration: 0.12 }, "<");
        tl.call(() => {
            urlEl!.textContent = URLS[i];
        });
        tl.to(urlEl!, { opacity: 1, duration: 0.12 });

        // 4. Step-specific animations during dwell
        if (i === 0) {
            // Cursor flies in from bottom-right and clicks the button
            tl.call(() => {
                const winEl = windows[0] as HTMLElement;
                const btnEl = organizeBtnEl as HTMLElement;
                // offsetLeft/Top gives position within #win0 (nearest positioned ancestor)
                // — same coordinate system as the cursor's position: absolute; top:0; left:0
                const tx = btnEl.offsetLeft + btnEl.offsetWidth / 2;
                const ty = btnEl.offsetTop + btnEl.offsetHeight / 2;
                const startX = winEl.offsetWidth * 0.78;
                const startY = winEl.offsetHeight * 0.82;
                cursorTarget.x = tx;
                cursorTarget.y = ty;
                gsap.set(winCursorEl!, { x: startX, y: startY, opacity: 1, scale: 1 });
            });
            tl.to(winCursorEl!, {
                x: () => cursorTarget.x,
                y: () => cursorTarget.y,
                duration: 0.85,
                ease: "power2.inOut",
            });
            tl.to({}, { duration: 0.25 }); // wait for cursor movement + settle
            // Click down
            tl.to(winCursorEl!, { scale: 0.8, duration: 0.08, ease: "power2.in" });
            tl.to(organizeBtnEl!, { scale: 0.94, duration: 0.08, ease: "power2.in" }, "<");
            // Click release
            tl.to(winCursorEl!, { scale: 1, duration: 0.12, ease: "back.out(1.5)" });
            tl.to(organizeBtnEl!, { scale: 1, duration: 0.12, ease: "back.out(1.5)" }, "<");
            tl.to({}, { duration: 0.6 }); // post-click pause
        } else if (i === 5) {
            // Scoreboard: red score 0→6, blue 0→2, timer 3:00→1:42 — all in parallel
            tl.to(scoreObj, {
                red: 6,
                duration: 2,
                ease: "steps(6)",
                onUpdate() {
                    scoreRedEl!.textContent = String(Math.round(scoreObj.red));
                },
            });
            tl.to(
                scoreObj,
                {
                    blue: 2,
                    duration: 1.2,
                    ease: "steps(2)",
                    onUpdate() {
                        scoreBlueEl!.textContent = String(Math.round(scoreObj.blue));
                    },
                },
                "<+=0.4",
            );
            tl.to(
                timerObj,
                {
                    val: 102,
                    duration: 2.5,
                    ease: "none",
                    onUpdate() {
                        timerEl!.textContent = formatTimer(timerObj.val);
                    },
                },
                "<",
            );
            tl.to({}, { duration: dwell - 2.5 }); // remaining dwell after animations
        } else if (i === 6) {
            // Results: rows stagger in, then published badge
            resultRows.forEach((row, ri) => {
                tl.to(
                    row!,
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.3,
                        ease: "power1.out",
                    },
                    "+=" + (ri === 0 ? 0.15 : 0.4),
                );
            });
            tl.call(() => {
                resultsPublishedEl!.classList.add("is-visible");
            });
            tl.to(resultsPublishedEl!, { opacity: 1, duration: 0.3, ease: "power1.out" });
            tl.to({}, { duration: 0.5 }); // brief hold after published badge
        } else {
            // Simple dwell
            tl.to({}, { duration: dwell });
        }

        // 5. Complete checklist item
        tl.call(complete, [i]);
    });

    // All-complete hold: 2s, then onRepeat fires resetAll
    tl.to({}, { duration: 2 });
}
